import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import { noshowAlertsAPI } from '@/services/noshowAlertsAPI';
import { NoshowSmsMessage } from '@/types/noshowAlerts';

import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Caption } from '@/components/Typography';

interface SmsConversationProps {
  alertId: number;
  onClose: () => void;
}

const SmsConversation = ({ alertId, onClose }: SmsConversationProps) => {
  const [messages, setMessages] = useState<NoshowSmsMessage[]>([]);
  const [loading, setLoading] = useState(true);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await noshowAlertsAPI.getMessages(alertId);
      if (response.success) {
        setMessages(response.data || []);
      }
    } catch (err: any) {
      console.error('[SmsConversation] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  return (
    <Modal onClose={onClose} title={`SMS Thread — Alert #${alertId}`}>
      <div style={{ padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
        {loading && <LoadingState overlayColor="white" />}
        {!loading && messages.length === 0 && (
          <Body color="light">No messages recorded yet.</Body>
        )}
        {!loading && (
          <Stack vertical gap={12}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems:
                    msg.direction === 'outbound' ? 'flex-end' : 'flex-start',
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius: '12px',
                    backgroundColor:
                      msg.direction === 'outbound' ? '#E8F4FD' : '#F3F4F6',
                  }}
                >
                  <Body>{msg.body}</Body>
                </div>
                <Caption color="lighter" style={{ marginTop: '2px' }}>
                  {msg.direction === 'outbound' ? 'Sent' : 'Received'}{' '}
                  {format(new Date(msg.created_at), 'MMM dd, HH:mm')}
                  {msg.message_type && ` · ${msg.message_type}`}
                </Caption>
              </div>
            ))}
          </Stack>
        )}
      </div>
    </Modal>
  );
};

export default SmsConversation;
